import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "tv_pwa_install_dismissed_at";  // non-sensitive: just a UI dismissal timestamp
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isIOS() {
  return typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function wasRecentlyDismissed() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const at = Number(raw);
  return !isNaN(at) && Date.now() - at < DISMISS_TTL_MS;
}

export default function InstallPWA() {
  const [prompt, setPrompt] = useState(null); // beforeinstallprompt event (Chrome/Edge)
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    // Android/Desktop Chromium
    function onBeforeInstall(e) {
      e.preventDefault();
      setPrompt(e);
      setVisible(true);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS: no beforeinstallprompt, show a hint after a short delay
    if (isIOS()) {
      const t = setTimeout(() => { setIosHint(true); setVisible(true); }, 1500);
      return () => { window.removeEventListener("beforeinstallprompt", onBeforeInstall); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }

  async function install() {
    if (!prompt) return;
    prompt.prompt();
    try {
      await prompt.userChoice;
    } catch (err) {
      // Browser rejects userChoice if the prompt was dismissed by the OS —
      // safe to swallow, but log for debuggability.
      console.debug("[pwa] install prompt userChoice rejected:", err?.message || err);
    }
    setPrompt(null);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-4 z-50 max-w-md w-[92%] glass border border-border rounded-2xl shadow-lg p-4 flex items-start gap-3 animate-fade-in-up"
      data-testid="pwa-install-banner"
    >
      <div className="w-10 h-10 shrink-0 rounded-xl bg-primary text-primary-foreground grid place-items-center">
        <Download className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base leading-tight">Install TravelVault</div>
        {iosHint ? (
          <p className="text-xs text-stone-600 mt-1">
            Tap <Share className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5" /> Share, then <b>Add to Home Screen</b>.
          </p>
        ) : (
          <p className="text-xs text-stone-600 mt-1">Add to your home screen for offline access at borders and hotels.</p>
        )}
      </div>
      {!iosHint && (
        <Button size="sm" onClick={install} className="rounded-full bg-primary text-primary-foreground" data-testid="pwa-install-btn">
          Install
        </Button>
      )}
      <button onClick={dismiss} className="text-stone-400 hover:text-stone-700 rounded-full p-1" data-testid="pwa-install-dismiss" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
